import { useState, useEffect, useContext } from "react";
import {
  Button,
  Container,
  Divider,
  Grid,
  Link,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import CustomDataGrid from "../components/CustomDataGrid";
import { PAGE_SIZE } from "../constants/Constants";
import moment from "moment";
import { AuthContext } from "../context/AuthProvider";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const DEFAULT_FILTER = {
  dateFrom: null,
  dateTo: null,
  type: "",
};

const TYPE_OPTIONS = ["B2C", "B2B"];

const ShopifyBackups = () => {
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const authContext = useContext(AuthContext);

  const handleReset = () => setFilter({ ...DEFAULT_FILTER });

  const loadData = async (resetPage) => {
    setIsLoading(true);

    let filters = {
      limit: PAGE_SIZE,
      offset: resetPage ? 0 : paginationModel.page * PAGE_SIZE,
    };
    if (filter.dateFrom && filter.dateTo) {
      filters.dateFrom = moment(filter.dateFrom).startOf("day").format();
      filters.dateTo = moment(filter.dateTo).endOf("day").format();
    }
    if (filter.type) {
      filters.type = filter.type;
    }
    const params = new URLSearchParams(filters);

    try {
      const response = await fetchWithInterceptor(
        `/api/product-backups?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );
      if (!response?.ok) {
        return;
      }
      const data = await response.json();
      setRows(Array.isArray(data?.logs) ? data.logs : []);
      setRowCount(Number(data?.total) || 0);
      if (resetPage) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    } catch (error) {
      console.error("Failed to load data", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadData(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleDownload = async (id) => {
    // Open the tab synchronously on click to avoid popup blockers, then point it
    // at the signed URL once we have it (no "noopener" so we keep the handle).
    const downloadWindow = window.open("", "_blank");
    if (downloadWindow) {
      downloadWindow.opener = null;
    }
    try {
      const response = await fetchWithInterceptor(
        `/api/product-backups/${id}/download`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );
      if (!response?.ok) {
        downloadWindow?.close();
        return;
      }
      const data = await response.json();
      if (data.url && downloadWindow) {
        downloadWindow.location.href = data.url;
      } else {
        downloadWindow?.close();
      }
    } catch (error) {
      downloadWindow?.close();
      console.error("Failed to download backup", error);
    }
  };

  const columns = [
    {
      field: "type",
      headerName: "Type",
      flex: 1,
      sortable: false,
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      sortable: false,
      valueGetter: (value) =>
        value ? format(new Date(value), "yyyy-MM-dd") : "",
    },
    {
      field: "download",
      headerName: "CSV file",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Link
          component="button"
          type="button"
          onClick={() => handleDownload(params.row.id)}
          underline="hover"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          <DownloadIcon fontSize="small" />
          Download
        </Link>
      ),
    },
  ];

  return (
    <>
      <Typography
        variant="body1"
        sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}
      >
        Daily backups of your Shopify products. Download any backup as a CSV file.
      </Typography>
      <Container component="main" maxWidth="100%" sx={{ pb: 3 }}>
        <Paper sx={{ width: "100%", bgcolor: "background.paper", mt: 2, mb: 2 }}>
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Available Backups</Typography>
            <br />
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Date from"
                  value={filter.dateFrom}
                  onChange={(dateFrom) => setFilter({ ...filter, dateFrom })}
                  format="yyyy-MM-dd"
                  slotProps={{ textField: { variant: "outlined", fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Date to"
                  value={filter.dateTo}
                  onChange={(dateTo) => setFilter({ ...filter, dateTo })}
                  format="yyyy-MM-dd"
                  slotProps={{ textField: { variant: "outlined", fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  select
                  label="Type"
                  variant="outlined"
                  fullWidth
                  value={filter.type}
                  onChange={(e) =>
                    setFilter({ ...filter, type: e.target.value })
                  }
                  slotProps={{ select: { autoComplete: "off" } }}
                >
                  <MenuItem value="">ALL</MenuItem>
                  {TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: "auto" }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleReset}>
                  RESET FILTERS
                </Button>
              </Grid>
            </Grid>
          </div>
          <Divider />

          <CustomDataGrid
            rows={rows}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            loading={isLoading}
          />
        </Paper>
      </Container>
    </>
  );
};

export default ShopifyBackups;
